import { createContext, useContext, type ReactNode } from 'react';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';

import { semanticColors, typographyTokens } from '@/theme/tokens';
import { CONTROL_HEIGHT, SECTION_CARD_SX } from './settingsStyles';

const SettingsSurfaceContext = createContext<'page' | 'dialog'>('page');

export const useSettingsSurface = () => useContext(SettingsSurfaceContext);

export const useSettingsControlHeight = () => CONTROL_HEIGHT[useSettingsSurface()];

export function SettingsSurfaceProvider({
  surface,
  children,
}: {
  surface: 'page' | 'dialog';
  children: ReactNode;
}) {
  return (
    <SettingsSurfaceContext.Provider value={surface}>{children}</SettingsSurfaceContext.Provider>
  );
}

export function SettingsSection({
  children,
  sx,
  labelledBy,
}: {
  children: ReactNode;
  sx?: SxProps<Theme>;
  labelledBy?: string;
}) {
  const surface = useSettingsSurface();
  return (
    <Box
      component={labelledBy ? 'section' : 'div'}
      aria-labelledby={labelledBy}
      sx={[
        surface === 'dialog' ? { mb: 2.5, '&:last-child': { mb: 0 } } : SECTION_CARD_SX,
        ...(Array.isArray(sx) ? sx : [sx ?? {}]),
      ]}
    >
      {children}
    </Box>
  );
}

export function SettingsRow({
  label,
  description,
  control,
  labelId,
  descriptionId,
  status,
  details,
  controlAlignment = 'center',
  divider = true,
  headerIcon,
}: {
  label: ReactNode;
  description?: ReactNode;
  control: ReactNode;
  labelId?: string;
  descriptionId?: string;
  status?: ReactNode;
  details?: ReactNode;
  controlAlignment?: 'center' | 'start';
  /** False for a panel's closing row, where a rule below has nothing to separate. */
  divider?: boolean;
  /** On the full page, makes the label its card's heading, styled like the other
   * card headings (icon, 15px bold). */
  headerIcon?: ReactNode;
}) {
  const surface = useSettingsSurface();
  const asCardHeader = surface === 'page' && Boolean(headerIcon);
  return (
    <Box
      role={details ? 'group' : undefined}
      aria-labelledby={details ? labelId : undefined}
      sx={{
        borderBottom: divider ? `1px solid ${semanticColors.borderLight}` : undefined,
        // A page card's last row needs no rule above the card's own edge.
        ...(surface === 'page' && { '&:last-child': { borderBottom: 'none' } }),
        py: asCardHeader ? 0 : surface === 'dialog' ? 1.5 : 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: controlAlignment === 'start' ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 180px', display: 'flex', gap: 1 }}>
          {asCardHeader && (
            <Box
              sx={{ mt: '2px', flexShrink: 0, lineHeight: 0, color: semanticColors.textPrimary }}
            >
              {headerIcon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              id={labelId}
              component={asCardHeader ? 'h2' : 'p'}
              sx={
                asCardHeader
                  ? { fontWeight: 700, fontSize: '15px', lineHeight: 1.3 }
                  : {
                      fontSize:
                        surface === 'dialog'
                          ? typographyTokens.settingsLabel.fontSize
                          : typographyTokens.body2.fontSize,
                      fontWeight: 500,
                    }
              }
            >
              {label}
            </Typography>
            {description && (
              <Typography
                id={descriptionId}
                sx={{
                  fontSize:
                    surface === 'dialog'
                      ? typographyTokens.body2.fontSize
                      : typographyTokens.label.fontSize,
                  lineHeight: 1.5,
                  color: semanticColors.textTertiary,
                  mt: 0.5,
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ flexShrink: 0, maxWidth: '100%', mt: controlAlignment === 'start' ? '2px' : 0 }}>
          {control}
        </Box>
      </Box>
      {details && <Box sx={{ mt: 1.5 }}>{details}</Box>}
      {status && (
        <Box
          sx={{
            mt: surface === 'dialog' ? 0.5 : 1,
            minHeight: surface === 'dialog' ? 24 : undefined,
          }}
        >
          {status}
        </Box>
      )}
    </Box>
  );
}
